import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { SkeletonLoaderComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { MEDIA_PARTNERS_DEFAULT_FILTER } from '../../../../shared/constants/impact-partners.constant';
import {
  MediaPartnerModel,
  MediaPartnersFilter,
} from '../../../../shared/models/impact-partners.model';
import { ImpactPartnersService } from '../../../../shared/services/impact-partners.service';
import { ScrollDisableService } from '../../../../shared/services/scroll-disable.service';
import { SharedModule } from '../../../../shared/shared.module';
import { AddEditPartnersComponent } from '../add-edit-partners/add-edit-partners.component';

@Component({
  selector: 'app-impact-partners',
  standalone: true,
  imports: [
    SharedModule,
    ConfirmationModalComponent,
    SkeletonLoaderComponent,
    PageHeaderComponent,
    ModalComponent,
    AddEditPartnersComponent,
  ],
  templateUrl: './impact-partners.component.html',
  styleUrl: './impact-partners.component.scss',
})
export class ImpactPartnersComponent implements OnInit, OnDestroy {
  isLoading: boolean = false;
  private partnersFilterSubject$: BehaviorSubject<MediaPartnersFilter> =
    new BehaviorSubject<MediaPartnersFilter>(MEDIA_PARTNERS_DEFAULT_FILTER);
  partnersFilter = this.partnersFilterSubject$.asObservable();
  partners: MediaPartnerModel[] = [];
  deletePartnerId: string = '';
  deleteLoading = false;
  showEditPartnerModal = false;
  editPartner: MediaPartnerModel | null = null;
  subscription: Subscription = new Subscription();
  partnersCount = 0;
  constructor(
    private readonly impactPartnersService: ImpactPartnersService,
    private readonly toastr: ToastrService,
    private readonly scrollDisableService: ScrollDisableService
  ) {}
  ngOnInit(): void {
    this.init();
  }

  loadMoreIfLastIndex() {
    const { pageNumber, limit } = this.partnersFilterSubject$.value;
    if (pageNumber * limit < this.partnersCount) {
      this.updatePartnersFilter({
        ...this.partnersFilterSubject$.value,
        limit,
        pageNumber: pageNumber + 1,
      });
    }
  }

  handleEditPartner(partner: any) {
    this.editPartner = partner;
    this.togglePartnerModal(true);
  }

  onDeletePartner(id: string) {
    this.deletePartnerId = id;
    this.scrollDisableService.stopScroll();
  }
  cancelDelete() {
    this.deletePartnerId = '';
    this.scrollDisableService.addScroll();
  }
  deletePartner() {
    if (this.deletePartnerId) {
      this.deleteLoading = true;
      this.impactPartnersService
        .deleteMediaPartner(this.deletePartnerId)
        .subscribe({
          next: (res) => {
            this.toastr.success('Media Partner deleted successfully.');
            this.updatePartnersFilter(MEDIA_PARTNERS_DEFAULT_FILTER);
            this.deletePartnerId = '';
            this.scrollDisableService.addScroll();
            this.deleteLoading = false;
          },
          error: (err) => {
            console.error(err);
            this.toastr.error(
              err?.error?.message || err?.message || 'Something went wrong!'
            );
            this.scrollDisableService.addScroll();
            this.deleteLoading = false;
          },
        });
    }
  }
  togglePartnerModal(state: boolean) {
    this.showEditPartnerModal = state;
    if (!state) {
      this.editPartner = null;
    }
  }

  init() {
    const sub = this.partnersFilterSubject$.subscribe(
      (value: MediaPartnersFilter) => {
        this.getMediaPartnersList(value);
      }
    );
    this.subscription.add(sub);
  }

  getMediaPartnersList(filter: MediaPartnersFilter) {
    this.isLoading = true;
    this.impactPartnersService.getMediaPartners(filter).subscribe({
      next: (data) => {
        this.isLoading = false;
        this.partners =
          filter.pageNumber > 1 ? [...this.partners, ...data.data] : data.data;
        this.partnersCount = data?.meta?.total ?? 0;
      },
      error: (error) => {
        this.isLoading = false;
        this.toastr.error(
          error?.error?.message || error?.message || 'Something went wrong!'
        );
      },
    });
  }
  handleUpdate() {
    this.togglePartnerModal(false);
    this.updatePartnersFilter({
      ...this.partnersFilterSubject$.value,
      pageNumber: 1,
    });
  }

  updatePartnersFilter(filter: MediaPartnersFilter) {
    this.partnersFilterSubject$.next(filter);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
